// ═════════════════════════════════════════════════════════════════════════════
//  SPACY COACH · la pantalla del entrenador
//
//  El modelo visual es el Spacy de la landing de Business (BSpacyHub), que es
//  el que le gusta a Rob: el personaje grande, con sus gifs cambiando de estado
//  segun lo que este haciendo. La diferencia es la paleta — alli es cian porque
//  esa es la de Business; aqui es el amarillo de NSL, que es el del gimnasio.
//
//  ── EL REPARTO VOZ / PANTALLA ───────────────────────────────────────────────
//  La voz lleva la personalidad y el veredicto. La pantalla lleva los datos,
//  las listas y los numeros. No es un truco de coste: nadie quiere que le lean
//  en voz alta una lista de ejercicios. Por eso la racha, el nivel y los
//  minutos se PINTAN, y el coach los comenta.
//
//  ── POR QUE VIDEO Y NO GIF ──────────────────────────────────────────────────
//  Los gifs originales son de 1080x1080 y pesan 44 MB entre los cuatro; el de
//  hablar, el solo, 26,5 MB. Y se ven en un circulo de 190 px (140 en movil):
//  seis veces mas resolucion de la que nadie llega a ver.
//
//  Convertidos a MP4 a 512 px pesan 629 KB los cuatro —72 veces menos, y el de
//  hablar 249 veces menos— con la misma imagen a ese tamano. Un gif no se
//  puede comprimir de verdad porque no tiene compresion entre fotogramas: cada
//  cuadro se guarda casi entero. Un video si, y esa es toda la diferencia.
//
//  Como ahora pesan nada, se cargan los tres de golpe: el coach cambia de
//  estado al instante en vez de esperar a que baje el gif de hablar.
//
//  Los .gif siguen en assets/ porque los usan las dos landings. Migrarlas es
//  la siguiente mejora, y ahi el ahorro importa mas todavia: es lo primero que
//  descarga alguien que no conoce NSL.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// Los mismos iconos que la caja de Spacy en lecciones. Emoji no: el 🎙 y el
// 📨 los dibuja cada sistema a su manera y no cuadran con el resto de NSL.
import { Mic, Send, Settings2, Square, Volume2, VolumeX } from "lucide-react";
// El color del contador sale de aqui, la misma funcion que usa la leccion: si
// un dia se decide avisar antes, cambia en los dos sitios a la vez.
import { avisaDelTope } from "../limites";

// El recorrido de bienvenida vive APARTE, en su propia carpeta. Esta pantalla
// solo decide CUANDO se ensena; el que paso toca, que audio suena y si lo
// omitio es asunto suyo. Ver components/recorrido/RecorridoNSL.jsx.
import RecorridoNSL from "../components/recorrido/RecorridoNSL";
import { tocaRecorrido, haVistoElRecorrido } from "../components/recorrido/estado";
import Header from "../components/Header";
import api from "../api/apiClient";
import { useSubscriptionStatus } from "../components/guards/SubscriptionGuard";
import SpacyNormal from "../assets/spacy-normal.mp4";
import SpacyPensando from "../assets/spacy-pensando.mp4";
import SpacyHablando from "../assets/spacy-hablando.mp4";
import SpacyEscuchando from "../assets/spacy-escuchando.mp4";
import { useOidoCoach } from "../hooks/useOidoCoach";
// FLUENCY NEXUS, el estandar de diseno de juego de NSL. Se abre tal cual desde
// aqui: recibe `words` y ya sabe hacer el resto, asi que el coach solo tiene
// que darle la lista. Antes solo se podia entrar desde el Glosario.
import MiniGameModal from "../components/glossary/MiniGameModal";
import Confeti from "../components/Confeti";
import CoachAcciones from "../components/CoachAcciones";
// Lo que el coach ENSEÑA mientras habla: la nota, las destrezas, el marcador de
// la semana. Los datos vienen del backend ya armados, del ASISTENTE que se
// consulto, nunca de lo que escriba el modelo.
import CoachTarjeta from "../components/CoachTarjeta";
// El minijuego que te pone el coach, ENCIMA del chat. No es un enlace: al
// salir del juego el coach sigue ahi, igual que con FLUENCY NEXUS.
import CoachJuego, { existeJuego } from "../components/CoachJuego";
import CoachRutina from "../components/CoachRutina";
import CoachRutinaBoton from "../components/CoachRutinaBoton";
// ── LA ESPERA DE SPACY ──────────────────────────────────────────────────────
// El mismo par que usa la leccion: un haz de luz que da la vuelta al borde de
// la conversacion y una tarjeta con mensajes de lo que esta consultando, con
// su sonido. No se copia nada: se importa.
//
// Esto es, ademas, la correccion de un error mio. Cuando Rob pidio «la barra
// que recorre», la busque en las HOJAS de la leccion, no la encontre y me
// invente una franja sobre el avatar. Estaba en `components/SpacyScanner.jsx`
// desde el principio. La mia se retira: dos senales de "estoy pensando"
// compitiendo es peor que ninguna.
import { SpacyThinkingBubble, ScanBorderWrapper } from "../components/SpacyScanner";
import "../components/spacy-scanner.css";
// ── EL FORMATO DE SPACY ─────────────────────────────────────────────────────
// El coach escribe como escribe Spacy en lecciones: **negrita** para lo que
// importa, *cursiva* para citar un verbo, listas cuando enumera. Aqui se
// pintaba en crudo y el alumno leia los asteriscos: «el *to*, el *book*».
//
// El renderizador ya existe y es el MISMO que usa la leccion. No se copia ni se
// reescribe: se importa. Su hoja va aparte —la importa quien lo usa— y esta
// acotada al prefijo `spr-`, sin un solo selector suelto.
import SpacyMessageRenderer from "../exercises/SpacyMessageRenderer";
import "../exercises/spacy-renderer.css";
// Los efectos de sonido de NSL, los mismos que usan el Dojo y el Consultorio.
// Se usa ESE modulo y no uno propio para que el coach suene como el resto de la
// casa.
//
// Ojo con una cosa: `fx.js` tiene `setMuted`, pero NSL decidio en su dia QUITAR
// el silencio global —"el sonido es parte de la experiencia, como en Duolingo"—
// y de hecho borra esa preferencia al arrancar. Asi que el interruptor de esta
// pantalla NO toca eso: apaga la VOZ del coach, que es otra cosa y ademas es la
// cara. Mezclar las dos seria deshacer una decision que alguien tomo con motivo.
import { fxTap, fxAvanzar, fxFinish, fxDesbloqueo, fxVolver } from "../audio/fx";
// ── EL AUDIO DE LA BIENVENIDA SE BUSCA, NO SE EXIGE ───────────────────────
//  Los otros veintiuno se importan por su nombre porque llevan meses en disco.
//  Éste se graba con `backend-api/graba_saludo.py`, que cuesta dinero y por eso
//  lo lanza Rob a mano — así que mientras no esté, un `import` normal no
//  «faltaría un sonido»: rompería la compilación y con ella la aplicación
//  entera. Un detalle de la primera visita no puede tumbar el producto.
//
//  `import.meta.glob` no falla cuando no hay nada que casar: devuelve un objeto
//  vacío, la bienvenida sale escrita y muda, y el día que el fichero aparezca
//  suena sin que haya que tocar esta pantalla.
const GRABADOS_BIENVENIDA = import.meta.glob(
  "../assets/coach-saludo-bienvenida*.mp3",
  { eager: true, query: "?url", import: "default" },
);
const audioBienvenida = (sufijo) =>
  GRABADOS_BIENVENIDA[`../assets/coach-saludo-bienvenida${sufijo}-0.mp3`] || null;
import Saludo0 from "../assets/coach-saludo-0.mp3";
import Saludo1 from "../assets/coach-saludo-1.mp3";
import Saludo2 from "../assets/coach-saludo-2.mp3";
import Saludo3 from "../assets/coach-saludo-3.mp3";
import Saludo4 from "../assets/coach-saludo-4.mp3";
import Saludo5 from "../assets/coach-saludo-5.mp3";
import Saludo6 from "../assets/coach-saludo-6.mp3";
import Saludo7 from "../assets/coach-saludo-7.mp3";
import SaludoEn0 from "../assets/coach-saludo-en-0.mp3";
import SaludoEn1 from "../assets/coach-saludo-en-1.mp3";
import SaludoEn2 from "../assets/coach-saludo-en-2.mp3";
import SaludoEn3 from "../assets/coach-saludo-en-3.mp3";
import SaludoEn4 from "../assets/coach-saludo-en-4.mp3";
import SaludoEn5 from "../assets/coach-saludo-en-5.mp3";
import SaludoN0 from "../assets/coach-saludo-n-0.mp3";
import SaludoN1 from "../assets/coach-saludo-n-1.mp3";
import SaludoN2 from "../assets/coach-saludo-n-2.mp3";
import SaludoN3 from "../assets/coach-saludo-n-3.mp3";
import SaludoNEn0 from "../assets/coach-saludo-n-en-0.mp3";
import SaludoNEn1 from "../assets/coach-saludo-n-en-1.mp3";
import SaludoNEn2 from "../assets/coach-saludo-n-en-2.mp3";
import { textos } from "./coachTextos";
import "./CoachPage.css";

// Gemelo de `backend-api/src/coach/personalidad.py` · MAX_MENSAJE.
// Si cambia uno, cambia el otro.
const MAX_MENSAJE = 500;

// ── LOS SALUDOS ────────────────────────────────────────────────────────────
// Ocho, grabados una vez y servidos como assets. Es la tecnica de Siri, Alexa
// y las voces de GPT: la primera frase del asistente esta grabada, y por eso
// suena AL INSTANTE mientras el sistema despierta por detras. No es solo mas
// barato: es la mitad de por que esos asistentes parecen listos.
//
// Coste por alumno que entra: CERO. Antes cada arranque gastaba una llamada de
// cerebro y otra de voz sin que nadie hubiera pedido nada.
//
// OCHO Y NO UNO porque uno solo se convierte en muletilla a la tercera visita
// y el alumno deja de oirlo. Rotando, cada dia le recibe distinto.
//
// Y esto NO es el banco de audio que se descarto en su dia: aquello era
// enlatar las RESPUESTAS, y sigue descartado. Esto es la puerta de entrada.
//
// Sin nombre y sin genero: al estar grabados no pueden decir el nombre, y un
// "listo" dejaria fuera a la mitad de los alumnos.
//
// ⚠️  GEMELOS de `assets/coach-saludo-N.mp3`, en este mismo orden. Si se toca
//     un texto hay que volver a grabarlo con `backend-api/graba_saludo.py`, o
//     el coach dira una cosa y sonara otra.
// ── EL PRIMERO DE TODOS ────────────────────────────────────────────────────
//  Rob, 29-09-2026, viendo lo que recibe un alumno recién registrado: «me
//  gustaría mejor el saludo de bienvenido a NSL, como tu primer ejercicio es
//  darle clic al "cómo funciona" y te guiaré con lo que te espera».
//
//  Los ocho de abajo dan por hecho que la persona ya sabe dónde está: «¿por
//  dónde le entramos?» es una buena pregunta para quien conoce el gimnasio, y
//  ninguna para quien acaba de cruzar la puerta. A ése hay que enseñarle la
//  casa antes de pedirle que elija máquina.
//
//  Sale UNA temporada: mientras no haya terminado el recorrido ni lo haya
//  omitido. En cuanto hace una de las dos, vuelven los de siempre — seguir
//  dándole la bienvenida a quien ya se la dieron es el tipo de detalle que
//  delata que detrás no hay nadie mirando.
//
//  Sin nombre y sin marca de género, como los demás: están grabados, así que
//  no pueden decir el nombre, y un «bienvenido» dejaría fuera a media clase.
const SALUDO_BIENVENIDA = {
  texto: "¡Qué bueno tenerte por aquí! Esto es Non Stop Learning. Antes de "
       + "entrenar, dale a «Cómo funciona» aquí abajo: te enseño en un minuto "
       + "todo lo que te espera.",
  audio: audioBienvenida(""),
};

const SALUDO_BIENVENIDA_EN = {
  texto: "Great to have you here! This is Non Stop Learning. Before we train, "
       + "tap «How it works» down here — one minute and you'll know your way "
       + "around.",
  audio: audioBienvenida("-en"),
};

const SALUDOS = [
  { texto: "¡Ey! ¿Cómo vas hoy? Dime por dónde le entramos al entrenamiento.", audio: Saludo0 },
  { texto: "¡Qué onda! Aquí andamos con todo. ¿Con qué le damos hoy?", audio: Saludo1 },
  { texto: "¡Ey, ya llegaste! Dime qué quieres entrenar y nos ponemos.", audio: Saludo2 },
  { texto: "¿Qué tal va eso? Vamos a mover ese inglés. ¿Por dónde arrancamos?", audio: Saludo3 },
  { texto: "¡Ahí está! Me da gusto verte por aquí. ¿Qué se te antoja hoy?", audio: Saludo4 },
  { texto: "¡Ey! El gimnasio está abierto y yo también. Dime qué toca.", audio: Saludo5 },
  { texto: "¿Cómo andamos? Aquí tu coach, esperándote. ¿Qué quieres trabajar?", audio: Saludo6 },
  { texto: "¡Llegaste! Vamos a soltar ese inglés. ¿Por dónde empezamos?", audio: Saludo7 },
];

// Los mismos, en ingles. A un alumno que entrena en ingles, un saludo en
// espanol le rompe el modo antes de empezar.
const SALUDOS_EN = [
  { texto: "Hey! How's it going? Tell me where we start today.", audio: SaludoEn0 },
  { texto: "There you are! What do you feel like training today?", audio: SaludoEn1 },
  { texto: "Hey, you made it! Tell me what you want to work on.", audio: SaludoEn2 },
  { texto: "What's up? Let's move that English. Where do we begin?", audio: SaludoEn3 },
  { texto: "Hey! The gym is open and so am I. Tell me what you need.", audio: SaludoEn4 },
  { texto: "Good to see you! So, what are we training today?", audio: SaludoEn5 },
];

// ── LOS SALUDOS NEUTROS ────────────────────────────────────────────────────
// Los de arriba son de vestuario. Un «¡Qué onda! Aquí andamos con todo» delante
// de un trato profesional es incoherente: la primera frase promete una cosa y
// la segunda es otra.
//
// Cuatro y no ocho: en este tono la gracia no esta en variar el saludo, esta en
// ir al grano. Siguen sin marca de genero, igual que los otros.
//
// ⚠️  GEMELOS de `backend-api/graba_saludo.py` · SALUDOS_NEUTRO.
const SALUDOS_NEUTRO = [
  { texto: "Hola, ¿cómo vas? Dime por dónde quieres empezar hoy.", audio: SaludoN0 },
  { texto: "Aquí estamos. ¿Con qué quieres entrenar hoy?", audio: SaludoN1 },
  { texto: "Qué gusto verte. Dime qué te gustaría practicar.", audio: SaludoN2 },
  { texto: "Ya podemos empezar. ¿Por dónde arrancamos?", audio: SaludoN3 },
];

const SALUDOS_NEUTRO_EN = [
  { texto: "Hello. How are you doing? Tell me where you want to start today.", audio: SaludoNEn0 },
  { texto: "Good to see you. What would you like to practise today?", audio: SaludoNEn1 },
  { texto: "We can start whenever you want. Where do we begin?", audio: SaludoNEn2 },
];

// ── EL IDIOMA ──────────────────────────────────────────────────────────────
// NSL es un gimnasio de INGLES y el objetivo es que el alumno acabe entrenando
// en ingles. Pero el idioma no se pregunta en un formulario: por defecto sale
// de su NIVEL, el coach le sigue si el alumno cambia, y el propio coach le
// propone subir cuando ve que puede.
//
// Esto es solo la valvula manual, para el que quiera forzarlo. Va a la vista y
// no enterrada en ajustes: un ajuste que hay que buscar es un ajuste que nadie
// encuentra.
// Las etiquetas NO se traducen: "Auto", "ES", "MIX" y "EN" se leen igual en
// los dos idiomas y son las que el alumno ya reconoce. Lo que si cambia es la
// explicacion de cada modo, que vive en `coachTextos.js`.
const IDIOMAS = [
  { id: "auto", etiqueta: "Auto" },
  { id: "es", etiqueta: "ES" },
  { id: "mix", etiqueta: "MIX" },
  { id: "en", etiqueta: "EN" },
];

const CLAVE_IDIOMA = "nsl_coach_idioma";
const CLAVE_VOZ = "nsl_coach_voz";
const CLAVE_TONO = "nsl_coach_tono";
// La marca de "esta rutina ya se celebro". Lleva la fecha y la vuelta dentro,
// asi que una rutina nueva vuelve a tener derecho a su confeti.
const CLAVE_FIESTA = "nsl_coach_fiesta";

// ── COMO LE HABLA ──────────────────────────────────────────────────────────
// No es el idioma: es el trato. El coach nacio hablando de vestuario y eso no
// le vale a todo el mundo. En "auto" lo decide su expediente —quien viene de
// una empresa lo recibe neutro sin tocar nada— y aqui puede cambiarlo.
const TONOS = [
  { id: "auto", etiqueta: "Auto" },
  { id: "gimnasio", etiqueta: "GYM" },
  { id: "neutro", etiqueta: "NEUTRO" },
];

// ── LO QUE PUEDE PEDIRLE ───────────────────────────────────────────────────
// Una caja vacia que pregunta "¿que quieres?" es la pantalla mas dificil que
// existe: el alumno no sabe que puede pedir y no descubre nunca lo que el
// coach sabe hacer. Estos cuatro botones son el menu del gimnasio.
//
// Viven en `coachTextos.js` porque cambian de idioma con la pantalla, igual
// que todo lo demas. Y lo que MANDAN al coach cambia tambien: un boton que
// dice "Train" y manda "¿Qué me toca hoy?" recibe una respuesta en espanol y
// rompe el modo ingles en el primer clic.

export default function CoachPage() {
  const navigate = useNavigate();
  const [expediente, setExpediente] = useState(null);
  const [turnos, setTurnos] = useState([]);          // {de: 'coach'|'alumno', texto}

  // `null` | 'lineal' | 'menu'. Arranca en `lineal` la primera vez que este
  // alumno entra al coach, que es justo cuando merece la pena contarselo.
  const [recorrido, setRecorrido] = useState(() => (tocaRecorrido() ? "lineal" : null));
  // ── LA BOTONERA SE APARTA CUANDO SPACY CUENTA ALGO ──────────────────────
  //  Rob: «si doy clic se baja la barra de entrenador para ver lo que manda el
  //  coach». Y es que el menu del recorrido son once opciones DENTRO del hilo:
  //  con la botonera desplegada debajo, la conversacion se queda con un tercio
  //  de la pantalla justo cuando es lo unico que hay que mirar.
  //
  //  Es una senal y no un booleano a proposito: `CoachAcciones` decide solo si
  //  arranca abierta segun lo alta que sea la pantalla, y un booleano de fuera
  //  le quitaria esa decision. Esto solo le dice «pliegate ahora»; despues el
  //  alumno la vuelve a abrir cuando quiera.
  const [plegarBotonera, setPlegarBotonera] = useState(0);
  const apartaLaBotonera = () => setPlegarBotonera(n => n + 1);
  const [texto, setTexto] = useState("");
  const [pensando, setPensando] = useState(false);
  const [hablando, setHablando] = useState(false);
  // Si el coach habla o solo escribe. Es SU interruptor, no el del sonido de
  // NSL: apagar la voz ademas de callarle, deja de gastar —es la pata cara— y
  // hay sitios donde uno no puede tener el movil sonando.
  const [conVoz, setConVoz] = useState(() => {
    try { return localStorage.getItem(CLAVE_VOZ) !== "0"; } catch { return true; }
  });
  const [empezado, setEmpezado] = useState(false);
  const [avisoOido, setAvisoOido] = useState("");
  const [juego, setJuego] = useState(null);      // las palabras en juego
  // El minijuego abierto, si hay alguno. Guarda la carta entera —nombre, icono
  // y color— para que el marco se pinte del color del juego.
  const [minijuego, setMinijuego] = useState(null);
  const [confeti, setConfeti] = useState(false);
  // ── LA RUTINA DE HOY ──────────────────────────────────────────────────────
  // Se carga al entrar y no solo cuando el coach la monta: el boton de arriba
  // tiene que estar ahi al abrir la pantalla por la tarde, con lo que hizo por
  // la manana ya marcado. Una rutina que se evapora al recargar no es un plan.
  // ── ¿LE QUEDAN TOKENS? ────────────────────────────────────────────────────
  // El coach GASTA credito del alumno: cada turno se apunta en
  // `token_usage_logs` y de ahi sale su saldo del mes. Asi que un dia se
  // acaba, y hay que decirselo ANTES de que escriba — no despues de que el
  // servidor le cierre la puerta.
  //
  // Es el mismo guardia que usan los ejercicios de Spacy. Aqui no se bloquea
  // la pantalla entera a proposito: puede seguir abriendo su rutina, sus
  // juegos y su historial; lo unico que se apaga es HABLAR, que es lo unico
  // que cuesta dinero.
  const { status: estadoPlan } = useSubscriptionStatus();
  const [sinTokens, setSinTokens] = useState(false);
  const seQuedoSinTokens = sinTokens || estadoPlan === "tokens";

  const [rutina, setRutina] = useState(null);
  // Lo que hizo los ultimos catorce dias, en cifras. Viene con la rutina en la
  // misma respuesta: la hoja lo pinta abajo y pedirlo aparte serian dos viajes
  // para una sola pantalla.
  const [historial, setHistorial] = useState(null);
  const [rutinaAbierta, setRutinaAbierta] = useState(false);
  // Late solo mientras esta sin abrir. Un adorno que no se apaga deja de
  // significar "hay algo nuevo" y pasa a ser ruido de la esquina.
  const [rutinaNueva, setRutinaNueva] = useState(false);
  const [tono, setTono] = useState(() => {
    try { return localStorage.getItem(CLAVE_TONO) || "auto"; } catch { return "auto"; }
  });
  const [idioma, setIdioma] = useState(() => {
    // Se recuerda en el navegador, no en el servidor: asi el coach sigue
    // siendo de SOLO LECTURA. Si se pierde, se vuelve a "auto", que es la
    // opcion correcta de todas formas.
    try { return localStorage.getItem(CLAVE_IDIOMA) || "auto"; } catch { return "auto"; }
  });
  // ── EL IDIOMA QUE MANDA EN LA PANTALLA ────────────────────────────────────
  // El EFECTIVO, no el que este marcado. En "auto" manda su nivel: un alumno de
  // C1 que no ha tocado nada ve la pantalla en ingles, igual que le habla el
  // coach. Tener marcado "auto", oir ingles y leer espanol seria peor que no
  // traducir nada.
  //
  // MIX se queda en espanol a proposito: ahi el coach habla ingles pero se pasa
  // al espanol para rescatar al alumno cuando se atasca, y si la pantalla
  // tambien estuviera en ingles, el rescate no tendria donde apoyarse.
  const idiomaReal = idioma === "auto"
    ? (expediente?.idioma_sugerido || "es")
    : idioma;
  const T = textos(idiomaReal);

  // ── LOS BOTONES QUE VE ESTE ALUMNO ────────────────────────────────────────
  // Cuales y en que orden lo decide el EXPEDIENTE, no la pantalla: a uno que
  // acaba de llegar, "Mi examen" y "Mis fallos" no le dicen nada, y "Terminé"
  // le ofrece mentir sobre algo que no ha empezado. El backend manda
  // identificadores; los textos y los colores siguen aqui, que es donde cambian
  // de idioma.
  //
  // Si el expediente todavia no ha llegado —o viene de una version anterior—
  // se ensenan todos, que es como estaba antes. Nunca menos de lo que habia.
  const acciones = (() => {
    // ── LOS QUE NO DECIDE EL SERVIDOR ───────────────────────────────────────
    //  «Como se entrena aqui» no es una accion del coach: es la puerta de vuelta
    //  al recorrido, y el backend no sabe nada de ella. Al filtrar por el plan
    //  del expediente desaparecia, porque su `id` no viene en esa lista.
    //
    //  Rob lo vio en produccion el 26-09-2026 y en la prueba no salia: con la
    //  API cortada no hay plan, y sin plan se ensenan todas. El filtro solo
    //  muerde cuando el servidor SI contesta — que es justo el caso real.
    //
    //  Rob habia pedido que este boton estuviera «si o si», y tiene sentido
    //  literal: es el que rescata a quien se perdio. Que dependa de lo que
    //  opine el expediente de hoy es exactamente lo contrario.
    const locales = T.acciones.filter(a => a.local);

    const plan = expediente?.botones;
    if (!Array.isArray(plan) || plan.length === 0) return T.acciones;
    const porId = Object.fromEntries(T.acciones.map(a => [a.id, a]));
    const delPlan = plan
      .map(b => (porId[b.id] ? { ...porId[b.id], destacado: b.destacado } : null))
      .filter(Boolean)
      .map(vistiendoRutina);

    // Al final y sin duplicar: si algun dia el servidor decide incluirlo, se
    // respeta su sitio en el plan en vez de pintarlo dos veces.
    const yaEstan = new Set(delPlan.map(a => a.id));
    return [...delPlan, ...locales.filter(a => !yaEstan.has(a.id))];
  })();

  // ── ABRIR LA HOJA DE LA RUTINA ────────────────────────────────────────────
  // Si viene cerrada, confeti. UNA VEZ AL DIA y no cada vez que la abre: el
  // confeti dice "acabas de conseguir algo", y repetido cada vez que asoma la
  // cabeza deja de decir eso y pasa a ser un ruido que tapa la pantalla.
  //
  // La marca va en el navegador y lleva la fecha dentro. Si se pierde —otro
  // movil, datos borrados— lo peor que pasa es que la celebre dos veces, que
  // es un fallo mucho mas barato que no celebrarla nunca.
  const abreRutina = () => {
    fxDesbloqueo();
    setRutinaNueva(false);
    setRutinaAbierta(true);

    const pasos = rutina?.pasos || [];
    if (!pasos.length || !pasos.every(p => p.hecho_a_las)) return;
    const marca = `${CLAVE_FIESTA}:${rutina.fecha}:${rutina.vuelta || 1}`;
    try {
      if (localStorage.getItem(marca)) return;
      localStorage.setItem(marca, "1");
    } catch { /* sin memoria, se celebra igual */ }
    setConfeti(true);
  };

  // ── LO QUE PIDE DESDE LA HOJA ─────────────────────────────────────────────
  // Cierra la hoja y le habla al coach. No hay atajo que salte la conversacion
  // a proposito: el alumno tiene que VERLE trabajar —el haz del borde, los
  // mensajes de razonamiento— y encontrarse la rutina nueva al final. Montarla
  // por detras y avisar con un aviso seria mas rapido y mucho peor.
  const pideAlCoach = mensaje => {
    setRutinaAbierta(false);
    manda(mensaje);
  };

  // Quitar la rutina de hoy. NO borra la fila: lo que hizo antes de descartarla
  // sigue en su historial. Por eso el historial se recoge de la respuesta.
  const descartaRutina = () => {
    fxVolver();
    setRutinaAbierta(false);
    setRutina(null);
    api.post("/api/coach/rutina/descartar")
      .then(r => setHistorial(r.data?.historial || null))
      .catch(() => {});
  };

  // ── ABRIR UNA ESTACION DE LA RUTINA ───────────────────────────────────────
  // Se marca como hecha con el CLIC, que es lo unico que se puede medir: la
  // abrio desde su rutina, a esta hora. Nunca dice "aprobado" — los minijuegos
  // no guardan resultado por partida.
  //
  // Y se marca OPTIMISTA: el anillo se mueve al instante y el servidor se
  // entera despues. Si la peticion falla, lo peor que pasa es que manana la
  // fila diga que no la abrio; esperar medio segundo a que conteste, en cambio,
  // se nota en cada clic.
  const abreEstacion = paso => {
    fxTap();
    setRutina(r => (r ? {
      ...r,
      pasos: r.pasos.map(p => (p.id === paso.id && !p.hecho_a_las
        ? { ...p, hecho_a_las: new Date().toISOString() } : p)),
    } : r));
    api.post("/api/coach/rutina/hecho", { paso: paso.id }).catch(() => {});

    // Las que se abren ENCIMA no cierran la rutina del todo: el alumno vuelve
    // aqui al salir del juego y ve su siguiente estacion encendida. Las que
    // son otra pagina si la cierran, porque se va.
    if (paso.tipo === "vocabulario") { setRutinaAbierta(false); setJuego(paso.palabras); }
    else if (paso.tipo === "minijuego") {
      setRutinaAbierta(false);
      setMinijuego({ juego: paso.juego, nombre: paso.titulo,
                     icono: paso.icono, color: paso.color });
    } else if (paso.url) navigate(paso.url);
  };

  // ── EL BOTON DE LA RUTINA TIENE DOS CARAS ─────────────────────────────────
  // Sin rutina es una peticion: se la pide al coach y le ve montarla.
  // Con rutina es una PUERTA: abre la que ya tiene, sin gastar una llamada a
  // la IA. Pedirle otra vez lo que ya tienes cuesta dinero y no da nada.
  //
  // Y cerrada se apaga. Ahi es donde Rob tiene toda la razon: un boton que
  // grita cuando no hay nada que hacer detras enseña a no hacerle caso. Sigue
  // pulsable —querra ver su marcador y sus opciones— pero baja la voz.
  function vistiendoRutina(a) {
    const pasos = rutina?.pasos || [];
    if (!pasos.length) return a;
    const hechas = pasos.filter(p => p.hecho_a_las).length;
    const cerrada = hechas === pasos.length;

    if (a.id === "rutina") {
      return {
        ...a,
        sub: cerrada ? T.rutinaBotonCerrada : T.rutinaBotonAbrir(hechas, pasos.length),
        apagada: cerrada,
      };
    }

    // ── "ENTRENAR" DEJA DE COMPETIR CON LA RUTINA ─────────────────────────
    // Rob: «Mi rutina y Entrenar son lo mismo». Lo eran mientras los dos
    // acababan pidiendole una clase al coach. Con una rutina abierta, este
    // pasa a ser el ATAJO: entra directo en la siguiente estacion, sin abrir
    // la hoja y sin gastar una llamada a la IA.
    //
    // Asi cada uno tiene su verbo. "Mi rutina" es VER —el plan, el marcador,
    // las opciones— y "Entrenar" es HACER. Quien sabe lo que quiere pulsa el
    // segundo; quien quiere decidir, el primero.
    if (a.id === "toca" && !cerrada) {
      const siguiente = pasos.find(p => !p.hecho_a_las);
      return { ...a, sub: T.rutinaSigue(siguiente.titulo), atajo: siguiente };
    }
    return a;
  }

  const celebrado = useRef(false);
  const audioRef = useRef(null);
  const finalRef = useRef(null);
  const textoRef = useRef(null);
  const charlaRef = useRef(null);
  // El mismo idioma, pero en un `ref`. Los manejadores del oido y el `catch` de
  // `manda` se crean UNA vez: si leyeran `T` directamente se quedarian con el
  // idioma que hubiera al montar la pantalla, y un alumno que cambia a EN
  // seguiria viendo los avisos de error en espanol.
  const idiomaRealRef = useRef(idiomaReal);
  idiomaRealRef.current = idiomaReal;
  // Y el trato efectivo, por lo mismo: `empezar()` se crea una vez y tiene que
  // leer el de AHORA, no el de cuando se monto la pantalla.
  const tonoRealRef = useRef("gimnasio");
  tonoRealRef.current = tono === "auto"
    ? (expediente?.tono_sugerido || "gimnasio")
    : tono;
  const ajustesRef = useRef(null);

  // El idioma y la voz viven detras de un engrane. Antes ocupaban una barra
  // fija en mitad de la pantalla, y son ajustes: se tocan una vez y no se
  // vuelven a mirar. Es lo que hacen los asistentes de verdad.
  const [ajustes, setAjustes] = useState(false);

  // La caja crece con lo que se escribe y para a los 140px, igual que en
  // lecciones. Una caja fija de dos lineas esconde justo lo que el alumno
  // acaba de escribir cuando se pasa de ahi.
  function creceSola(el) {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.value === "" ? "40px" : `${el.scrollHeight}px`;
  }

  // Lo que oye va a la CAJA, no al coach: el alumno lo ve y puede corregirlo
  // antes de mandarlo. Una transcripcion mala que se envia sola convierte un
  // fallo del microfono en un coach que parece tonto, y el alumno no tiene
  // forma de saber cual de las dos cosas paso.
  const oido = useOidoCoach({
    onTexto: (t) => {
      setAvisoOido("");
      setTexto(prev => (prev ? `${prev} ${t}` : t).slice(0, MAX_MENSAJE));
      // El texto entra por programa, asi que `onChange` no salta y la caja no
      // creceria sola: lo que acaba de dictar quedaria fuera de la vista.
      setTimeout(() => creceSola(textoRef.current), 0);
    },
    // Se lee del `ref` y no de `T`: este manejador se crea una vez y se
    // quedaria con el idioma que hubiera al montar la pantalla.
    onError: (motivo) => setAvisoOido(
      motivo === "not-allowed" || motivo === "service-not-allowed"
        ? textos(idiomaRealRef.current).microDenegado
        : textos(idiomaRealRef.current).microNoEntendio
    ),
  });

  // El expediente se pide aparte y no gasta IA: la pantalla puede pintar la
  // racha y el nivel sin necesidad de hacer hablar al coach.
  useEffect(() => {
    api.get("/api/coach/expediente")
      .then(r => setExpediente(r.data))
      .catch(() => setExpediente(null));
    // ── LA CONVERSACION DE ANTES ────────────────────────────────────────
    // Rob cerro el navegador y volvio a una pantalla en blanco: la charla vivia
    // solo en la memoria de la pestaña. Ahora se recupera del servidor, y si
    // hay algo NO se pasa por la pantalla de "Comenzar" — ya empezo, hace
    // dias. Entrar a tu propia conversacion no se hace con un boton.
    api.get("/api/coach/conversacion")
      .then(r => {
        const guardados = r.data?.mensajes || [];
        if (guardados.length) {
          setTurnos(guardados);
          setEmpezado(true);
        }
      })
      .catch(() => { /* sin memoria se empieza como siempre */ });

    // Si no hay rutina de hoy responde `null` y no se pinta boton. La de ayer
    // no vuelve nunca, ni aunque quedara a medias: la decide el backend.
    api.get("/api/coach/rutina")
      .then(r => {
        setRutina(r.data?.rutina || null);
        setHistorial(r.data?.historial || null);
      })
      .catch(() => setRutina(null));
  }, []);

  // ── EL AUTO-SCROLL, PEGADO AL FONDO ────────────────────────────────────────
  // La charla tiene tope y scroll propio, asi que se mueve ELLA. Con
  // `scrollIntoView` se arrastraba tambien la pagina: cada respuesta empujaba
  // al alumno hacia abajo y le sacaba al coach de la vista.
  //
  // ── POR QUE NO BASTA CON BAJAR AL LLEGAR UN MENSAJE ────────────────────────
  // Rob: «cuando salen las frases de comparando, viendo examen, etc., no se
  // hace el auto scroll y no se ven».
  //
  // Y es exacto: al encenderse `pensando` se bajaba UNA vez, pero lo que hay
  // que ver aparece DESPUES —la tarjeta de "estoy consultando" se monta sola,
  // el mensaje va rotando y cambia de alto, y luego llega la tarjeta de datos,
  // que mide 200px—. Cada una de esas cosas crece por debajo del borde y el
  // scroll ya habia terminado.
  //
  // Asi que en vez de bajar en momentos concretos, se VIGILA el tamano: cada
  // vez que el contenido crece, si el alumno estaba abajo, se le mantiene
  // abajo. Es lo que hace cualquier chat.
  const pegadoAbajo = useRef(true);

  useEffect(() => {
    const c = charlaRef.current;
    if (!c) return;

    // Si el alumno ha subido a releer, NO se le arrastra. Un chat que te
    // devuelve al fondo mientras lees hacia arriba es insufrible; 60px de
    // margen es lo que separa "estoy abajo" de "he subido a mirar algo".
    const mira = () => {
      pegadoAbajo.current =
        c.scrollHeight - c.scrollTop - c.clientHeight < 60;
    };
    c.addEventListener("scroll", mira, { passive: true });

    const abajo = () => {
      if (pegadoAbajo.current) c.scrollTop = c.scrollHeight;
    };

    // Vigila TODO lo que crece dentro: la tarjeta de razonamiento, sus
    // mensajes rotando, la tarjeta de datos y los propios mensajes.
    const ojo = new ResizeObserver(abajo);
    ojo.observe(c);
    for (const hijo of c.children) ojo.observe(hijo);

    // Y uno para cuando se anaden hijos nuevos, que el de tamano no ve.
    const ojoHijos = new MutationObserver(() => {
      for (const hijo of c.children) ojo.observe(hijo);
      abajo();
    });
    ojoHijos.observe(c, { childList: true, subtree: true });

    return () => {
      c.removeEventListener("scroll", mira);
      ojo.disconnect();
      ojoHijos.disconnect();
    };
  }, [empezado]);

  // Al mandar un mensaje EL, se vuelve abajo siempre, aunque estuviera
  // releyendo: acaba de escribir, quiere ver que pasa.
  //
  // Cuando el que habla es el coach NO se fuerza: de eso ya se encarga el
  // vigilante de arriba, que baja solo si el alumno estaba abajo. Aqui estaba
  // `pensando` en la lista y era un error: al empezar a pensar arrastraba al
  // alumno al fondo aunque hubiera subido a releer una respuesta anterior.
  useEffect(() => {
    const c = charlaRef.current;
    if (!c) return;
    if (turnos[turnos.length - 1]?.de !== "alumno") return;
    pegadoAbajo.current = true;
    c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
  }, [turnos]);

  // El panel se cierra al tocar fuera o con Escape, como cualquier menu. Sin
  // esto se queda abierto tapando la conversacion.
  useEffect(() => {
    if (!ajustes) return;
    const fuera = ev => {
      if (ajustesRef.current && !ajustesRef.current.contains(ev.target)) setAjustes(false);
    };
    const escape = ev => { if (ev.key === "Escape") setAjustes(false); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", escape);
    };
  }, [ajustes]);

  // ── El confeti ────────────────────────────────────────────────────────
  // Lo dispara el DATO —una leccion cerrada al 100% hace menos de tres
  // horas—, nunca lo que diga el coach. Si dependiera del modelo, algun dia
  // saldria confeti por una clase a medias, y celebrar algo que no hiciste es
  // peor que no celebrar: te dice que el sistema no se entera.
  //
  // Y una sola vez por visita: `celebrado` lo impide en las respuestas
  // siguientes, porque la leccion sigue estando recien cerrada durante horas y
  // si no, saldria confeti en cada mensaje.
  useEffect(() => {
    if (expediente?.recien_cerrada && empezado && !celebrado.current) {
      celebrado.current = true;
      setConfeti(true);
    }
  }, [expediente, empezado]);

  function videoActual() {
    if (oido.escuchando) return SpacyEscuchando;
    if (hablando) return SpacyHablando;
    if (pensando) return SpacyPensando;
    return SpacyNormal;
  }

  function suena(audioB64) {
    if (!audioB64) return;
    try {
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(`data:audio/mpeg;base64,${audioB64}`);
      audioRef.current = a;
      setHablando(true);
      a.onended = () => setHablando(false);
      a.onerror = () => setHablando(false);
      a.play().catch(() => setHablando(false));
    } catch {
      setHablando(false);
    }
  }

  async function manda(mensaje) {
    const limpio = (mensaje || "").trim().slice(0, MAX_MENSAJE);
    if (!limpio || pensando) return;

    // ── LA CONVERSACION SE ABRE SOLA ──────────────────────────────────────
    // Rob pulso "¿Cómo voy?" en la hoja de la rutina con la pantalla todavia
    // en "Comenzar entrenamiento". El mensaje salio, el coach contesto y hasta
    // se le oyo hablar — pero la conversacion estaba oculta detras del boton
    // de arranque, asi que no vio nada.
    //
    // Cualquier camino que hable con el coach abre la conversacion. Estaba
    // solo en `empezar()`, y el dia que aparecio un segundo camino —la hoja de
    // la rutina— se rompio en silencio.
    if (!empezado) setEmpezado(true);

    if (oido.escuchando) oido.parar();
    setTurnos(t => [...t, { de: "alumno", texto: limpio }]);
    setTexto("");
    // Vaciar el valor no encoge la caja: la altura se puso a mano al crecer.
    if (textoRef.current) textoRef.current.style.height = "40px";
    setAvisoOido("");
    setPensando(true);

    // ── EL HISTORIAL YA NO SE MANDA ───────────────────────────────────────
    // Lo lee el servidor de su propia base. Antes viajaba desde aqui CON SU
    // ROL, asi que cualquiera con la consola abierta podia escribir lo que el
    // coach habia "dicho" antes y ponerle palabras en la boca — la inyeccion
    // mas fuerte que hay, porque un modelo se cree a si mismo antes que a
    // nadie. El servidor lo ignora aunque llegue; aqui se deja de enviar para
    // no pagar el viaje de ocho mensajes que nadie va a leer.
    try {
      const r = await api.post("/api/coach/hablar", {
        mensaje: limpio, con_voz: conVoz, idioma, tono,
      });
      const dijo = r.data?.coach || "";
      // Un toque al llegar la respuesta, distinto segun lo que traiga. La
      // TARJETA manda sobre la accion: es lo que aparece en pantalla y lo que
      // hace levantar la vista, asi que se lleva el sonido de desbloqueo.
      if (r.data?.tarjeta) fxDesbloqueo();
      else if (r.data?.accion) fxAvanzar();
      else fxFinish();
      // La clase viene del backend, del asistente `proponer_clase`. El modelo
      // nunca escribe un enlace: si lo hiciera, un dia mandaria al alumno a
      // una leccion que no existe.
      setTurnos(t => [...t, {
        de: "coach",
        texto: dijo,
        clase: r.data?.accion || null,
        tarjeta: r.data?.tarjeta || null,
      }]);
      if (r.data?.expediente) setExpediente(r.data.expediente);
      // El coach acaba de montarla: se recoge y el boton de arriba se pone a
      // latir. El plan entero NO viaja en la respuesta del chat —lleva las
      // palabras del juego y los enlaces—, asi que se pide a su ruta.
      if (r.data?.accion?.tipo === "rutina") {
        api.get("/api/coach/rutina")
          .then(x => {
            if (x.data?.rutina) { setRutina(x.data.rutina); setRutinaNueva(true); }
            if (x.data?.historial) setHistorial(x.data.historial);
          })
          .catch(() => { /* el boton se queda como estaba */ });
      }
      if (conVoz) suena(r.data?.audio_b64);
    } catch (e) {
      // ── LO QUE DE VERDAD PASO ─────────────────────────────────────────
      // Antes solo se entendia el 402 y todo lo demas se contaba como "no hay
      // senal". El dia que a un alumno se le acababan los tokens veia un error
      // de conexion y creia que la app estaba rota: el guardia devuelve 429,
      // no 402.
      //
      // Y hay DOS 429 distintos. El del guardia trae
      // `subscription_status: "tokens"`; el otro es el limitador diciendo que
      // va muy rapido. Contarlos como lo mismo mandaria a comprar tokens a
      // quien solo tiene que esperar tres segundos.
      const T2 = textos(idiomaRealRef.current);
      const codigo = e?.response?.status;
      const detalle = e?.response?.data?.detail;
      const esFalta = detalle?.subscription_status === "tokens";
      if (esFalta) setSinTokens(true);
      setTurnos(t => [...t, {
        de: "coach",
        texto: esFalta ? T2.sinTokensChat
          : codigo === 402 ? T2.planInactivo
          : codigo === 429 ? T2.demasiadoRapido
          : T2.sinSenal,
      }]);
    } finally {
      setPensando(false);
    }
  }

  function suenaSaludo(audio) {
    //  Puede no haber fichero: la bienvenida se graba aparte y hasta que esté,
    //  `audioBienvenida()` devuelve null. Sin esta salida, `new Audio(null)`
    //  pide "/null" al servidor y deja al muñeco con la boca abierta, porque
    //  `hablando` se enciende y nunca llega el `onended` que lo apaga.
    if (!audio) { setHablando(false); return; }
    try {
      const a = new Audio(audio);
      audioRef.current = a;
      setHablando(true);
      a.onended = () => setHablando(false);
      a.onerror = () => setHablando(false);
      a.play().catch(() => setHablando(false));
    } catch {
      setHablando(false);
    }
  }

  // ── El arranque ────────────────────────────────────────────────────────
  // El coach SALUDA y espera. Antes se enviaba un "Hola coach, vengo a
  // entrenar" en nombre del alumno y el coach contestaba a algo que el alumno
  // no habia dicho: se saltaba la presentacion y ademas gastaba dos llamadas
  // antes de que nadie hubiera pedido nada.
  //
  // Ningun asistente de verdad pone palabras en tu boca: abre el, decides tu.
  function empezar() {
    // Se elige AQUI y no al montar la pantalla: si se eligiera arriba, React
    // podria recalcularlo en un repintado y el texto dejaria de cuadrar con el
    // audio que ya esta sonando.
    // El idioma real: en "auto" manda su nivel. Los modos MIX y EN saludan en
    // ingles, porque en los dos el coach va a hablar en ingles.
    // El saludo tiene que casar con el TRATO, no solo con el idioma: el que
    // entra en neutro no puede recibir un "¡Qué onda!" y luego un coach que le
    // habla de usted en la practica.
    const enEspanol = idiomaRealRef.current === "es";
    const neutro = tonoRealRef.current === "neutro";
    const bolsa = neutro
      ? (enEspanol ? SALUDOS_NEUTRO : SALUDOS_NEUTRO_EN)
      : (enEspanol ? SALUDOS : SALUDOS_EN);

    //  Quien no ha visto el recorrido ENTERO recibe la bienvenida y no uno de
    //  los ocho de siempre.
    //
    //  Se pregunta por «terminado» y no por `tocaRecorrido()`, que es lo que
    //  parecía natural y no servía: mientras `tocaRecorrido()` es true, en esta
    //  pantalla está el recorrido y no el botón de «Comenzar», así que el
    //  saludo no se elige nunca. Al llegar aquí siempre era false y la
    //  bienvenida no la habría visto nadie.
    //
    //  A quien lo OMITE sí le toca: dejó de tenerlo delante, pero sigue sin
    //  saber qué hay en el gimnasio, y «¿por dónde le entramos?» es una
    //  pregunta imposible para quien no conoce las salas.
    //
    //  Y también en trato neutro: la bienvenida no tiene guasa de vestuario,
    //  sólo dice dónde está y por dónde empezar, así que no desentona.
    const s = !haVistoElRecorrido()
      ? (enEspanol ? SALUDO_BIENVENIDA : SALUDO_BIENVENIDA_EN)
      : bolsa[Math.floor(Math.random() * bolsa.length)];
    setEmpezado(true);
    setTurnos([{ de: "coach", texto: s.texto }]);
    if (conVoz) suenaSaludo(s.audio);
  }

  const e = expediente;

  return (
    <div className="cch-root">
      <Header />

      <main className="cch-main">
        {/* ── El personaje ─────────────────────────────────────────────── */}
        <section className="cch-escena">
          {/* ── LOS AJUSTES ─────────────────────────────────────────────────
              El idioma y la voz eran una barra fija en mitad de la pantalla.
              Son AJUSTES: se tocan una vez y no se vuelven a mirar en toda la
              sesion, asi que no pueden pagar peaje de espacio en cada turno.

              Aqui van anclados a la esquina de la escena, en posicion absoluta:
              cuestan CERO altura. Es como lo resuelven los asistentes de
              verdad —la conversacion manda, la configuracion espera detras de
              un boton— y ademas ahora se puede elegir el idioma ANTES de
              empezar, cosa que antes era imposible porque la barra solo salia
              con la conversacion ya abierta. Quien entrena en ingles ya no
              recibe el saludo en espanol.                                  */}
          <div className="cch-ajustes" ref={ajustesRef}>
            {/* Los dos botones de la esquina, en fila. La rutina va a la
                izquierda del engrane: se pulsa mucho mas, y en apaisado —donde
                esta esquina se va al lado contrario— sigue quedando la primera
                en el sentido de lectura. */}
            <div className="cch-botonera">
            <CoachRutinaBoton
              rutina={rutina}
              late={rutinaNueva}
              textos={T}
              onAbrir={abreRutina}
            />
            <button
              className={`cch-engrane ${ajustes ? "cch-engrane-on" : ""}`}
              onClick={() => { fxTap(); setAjustes(v => !v); }}
              aria-expanded={ajustes}
              aria-label={T.ajustes}
              title={T.idiomaYVoz}
            >
              <Settings2 size={18} />
            </button>
            </div>

            {ajustes && (
              <div className="cch-panel" role="group" aria-label={T.ajustes}>
                <p className="cch-panel-tit">{T.idioma}</p>
                <div className="cch-idiomas">
                  {IDIOMAS.map(i => (
                    <button
                      key={i.id}
                      className={`cch-idioma ${idioma === i.id ? "cch-idioma-on" : ""}`}
                      title={T.ayudaIdioma[i.id]}
                      onClick={() => {
                        setIdioma(i.id);
                        try { localStorage.setItem(CLAVE_IDIOMA, i.id); } catch { /* da igual */ }
                      }}
                    >
                      {i.etiqueta}
                    </button>
                  ))}
                </div>
                {/* La ayuda del idioma elegido, escrita. En la barra vivia en un
                    `title` que en un movil no ve nadie. */}
                <p className="cch-panel-pie">
                  {T.ayudaIdioma[idioma]}
                </p>

                {/* ── EL TRATO ──────────────────────────────────────────
                    Va junto al idioma porque contestan a la misma pregunta
                    —como te habla el coach— y ninguna se toca por mensaje.

                    Existe porque no se puede adivinar: NSL no guarda ni el
                    genero ni la edad del alumno, y deducirlos del nombre seria
                    peor que no hacer nada. Lo que si consta es quien viene de
                    una empresa, y a ese le llega ya en neutro. */}
                <p className="cch-panel-tit cch-panel-tit-2">{T.tono}</p>
                <div className="cch-idiomas">
                  {TONOS.map(t => (
                    <button
                      key={t.id}
                      className={`cch-idioma ${tono === t.id ? "cch-idioma-on" : ""}`}
                      onClick={() => {
                        setTono(t.id);
                        try { localStorage.setItem(CLAVE_TONO, t.id); }
                        catch { /* modo privado: solo dura la sesion */ }
                      }}
                    >
                      {t.id === "auto" ? t.etiqueta
                        : t.id === "gimnasio" ? T.tonoGimnasio : T.tonoNeutro}
                    </button>
                  ))}
                </div>
                <p className="cch-panel-pie">
                  {tono === "auto"
                    ? T.ayudaTono[e?.tono_sugerido || "gimnasio"]
                    : T.ayudaTono[tono]}
                </p>

                {/* Apaga la VOZ del coach, no los efectos de NSL: son cosas
                    distintas y estan a proposito separadas. */}
                <button
                  className={`cch-panel-voz ${conVoz ? "cch-panel-voz-on" : ""}`}
                  onClick={() => {
                    const nuevo = !conVoz;
                    setConVoz(nuevo);
                    try { localStorage.setItem(CLAVE_VOZ, nuevo ? "1" : "0"); }
                    catch { /* modo privado: solo dura la sesion */ }
                    if (nuevo) fxTap();
                    else if (audioRef.current) audioRef.current.pause();
                  }}
                  aria-pressed={conVoz}
                >
                  {conVoz ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  <span className="cch-panel-voz-eti">
                    {conVoz ? T.vozEncendida : T.vozApagada}
                  </span>
                  {/* Un interruptor y no un boton relleno de amarillo: encendida
                      es el estado NORMAL, y pintar lo normal del color de las
                      acciones convierte el ajuste en el grito del panel. */}
                  <span className="cch-interruptor" aria-hidden="true"><i /></span>
                </button>
              </div>
            )}
          </div>

          {/* El orbe: el aura difuminada detras y el personaje dentro de su
              contorno. Es el tratamiento del Spacy de la landing de Business
              —el que le gusta a Rob— en el amarillo de NSL.

              El aura va FUERA del avatar y no dentro. El avatar recorta
              (`overflow: hidden`) para que el video salga redondo, asi que
              metida dentro se cortaria justo en el borde, que es donde tiene
              que verse. */}
          {/* El personaje a un lado y su identidad al otro. Apilados se
              llevaban 214px de alto antes de que empezara la conversacion, que
              es lo que el alumno viene a leer. */}
          <div className="cch-presenta">
          <div className="cch-orbe">
            <div className="cch-aura" aria-hidden="true" />
            <div className="cch-avatar">
              {/* `key` obliga a React a rehacer el elemento al cambiar de
                  estado; sin el, el navegador se queda con el video anterior.
                  `muted` y `playsInline` no son opcionales: sin los dos, el
                  movil no deja que un video arranque solo. */}
              <video
                key={videoActual()}
                className="cch-img"
                src={videoActual()}
                autoPlay
                loop
                muted
                playsInline
                aria-label="Spacy Coach"
              />
              <div className="cch-brillo" aria-hidden="true" />

              </div>
          </div>

          <div className="cch-identidad">
          <h1 className="cch-nombre">
            <span className="cch-chapa">Spacy Coach</span>
          </h1>

          {/* ── LA FICHA ────────────────────────────────────────────────
              Los datos se siguen pintando —la voz nunca lee una lista de
              numeros en voz alta— pero ya no en una tarjeta propia.

              Antes eran un bloque hundido con cifras enormes debajo del
              nombre: en un movil se comian 61px y competian de tu a tu con el
              personaje. Y no son el foco; son la credencial del alumno.

              Asi que bajan de rango a UNA linea, pegada al nombre, como el
              subtitulo de una ficha. Se ven igual —siguen ahi, sin abrir nada—
              pero dejan de disputarle el centro a Spacy. */}
          {e && !e.es_nuevo && (
            <p className="cch-ficha">
              {e.racha_dias > 0 && (
                <span className="cch-ficha-dato">
                  <b>{e.racha_dias}</b> {T.diasSeguidos}
                </span>
              )}
              {e.nivel && (
                <span className="cch-ficha-dato"><b>{e.nivel}</b> {T.tuNivel}</span>
              )}
              <span className="cch-ficha-dato">
                <b>{e.minutos_entrenados}</b> {T.minutos}
              </span>
              <span className="cch-ficha-dato"><b>{e.xp}</b> {T.xp}</span>
            </p>
          )}
          </div>
          </div>
        </section>

        {/* ── EL DIALOGO ──────────────────────────────────────────────────
            Todo lo que no es el personaje va aqui dentro: la charla, los
            botones, la caja y el enlace de vuelta.

            No es un `div` de adorno. En APAISADO esta caja se pone AL LADO
            de Spacy en vez de debajo, y ahi esta la diferencia entre que
            quepa todo o no: en un movil tumbado hay 390px de alto y el
            personaje solo ya se comia la mitad. Sin este envoltorio no habria
            nada que poner en la segunda columna.                          */}
        <div className="cch-dialogo">
        {/* ── Antes de empezar, Spacy no habla ─────────────────────────── */}
        {!empezado ? (
          <section className="cch-arranque">
            {/* ── EL RECORRIDO, ANTES DE LA PRIMERA CONVERSACIÓN ──────────
                Aquí y no dentro del chat: el chat no existe hasta que se pulsa
                «Comenzar», así que una tarjeta metida ahí dentro no la vería
                nunca quien acaba de llegar — que es exactamente a quien va
                dirigida. Y así tampoco gasta una llamada a la IA para
                arrancar: el recorrido no necesita al coach para hablar.

                El Coach sólo decide CUÁNDO; el resto vive en
                `components/recorrido/`. */}
            {recorrido ? (
              <RecorridoNSL
                modo={recorrido}
                //  El recorrido puede pedir que, al cerrarse, la
                //  conversación arranque sola. Lo pide su botón final: quien
                //  acaba de terminarlo no tiene que pulsar «Comenzar
                //  entrenamiento» para seguir hablando con el mismo Spacy que
                //  lleva once pasos hablándole.
                //
                //  Al omitirlo NO se arranca: ahí la persona dijo que no
                //  quería, y meterla en una conversación es lo contrario.
                onCerrar={(op) => {
                  setRecorrido(null);
                  if (op?.arrancar) empezar();
                }}
                onHabla={setHablando}
              />
            ) : (
              <>
                <p className="cch-arranque-texto">
                  {e?.es_nuevo ? T.esperandoNuevo : T.esperandoVuelve}
                </p>
                <button className="cch-empezar" onClick={empezar}>
                  {T.comenzar}
                </button>
              </>
            )}
          </section>
        ) : (
          <>
            {/* ── La conversación ──────────────────────────────────────────
                Envuelta por el haz que recorre el borde mientras Spacy piensa.
                El envoltorio hereda el `flex` a proposito —lo dice su propio
                CSS— asi que meterlo NO rompe el reparto de altura de la
                pantalla. */}
            <ScanBorderWrapper isActive={pensando}>
            <section className="cch-charla" ref={charlaRef}>
              {turnos.map((t, i) => (
                <div key={i} className="cch-fila">
                  {/* Lo del coach pasa por el renderizador; lo del alumno, no.
                      El alumno no escribe en markdown, y si se le renderizara,
                      un asterisco suyo se convertiria en cursiva y su propio
                      mensaje le saldria distinto de como lo escribio. */}
                  <div className={`cch-turno cch-turno-${t.de}`}>
                    {t.de === "coach"
                      ? <SpacyMessageRenderer text={t.texto} />
                      : t.texto}
                  </div>

                  {/* Lo que el coach ENSEÑA. Va debajo de lo que dijo y encima
                      del boton: primero te lo cuenta, luego te lo ensena, y al
                      final te dice que hacer. */}
                  {t.tarjeta && <CoachTarjeta tarjeta={t.tarjeta} />}

                  {/* ── EL MINIJUEGO QUE LE PONE ─────────────────────────
                      Una carta con el color del juego, su icono y —lo que
                      importa— POR QUE se lo pone. Esa frase es la diferencia
                      entre el menu de juegos, que ensena once iconos y te deja
                      elegir, y un coach que ha mirado tu examen. */}
                  {t.clase?.tipo === "minijuego" && existeJuego(t.clase.juego) && (
                    <>
                      <button
                        className="cch-mini"
                        style={{ "--juego-color": t.clase.color }}
                        onClick={() => { fxTap(); setMinijuego(t.clase); }}
                      >
                        <span className="cch-mini-icono" aria-hidden="true">
                          {t.clase.icono}
                        </span>
                        <span className="cch-mini-texto">
                          <span className="cch-mini-eti">{T.juegoParaTi}</span>
                          <span className="cch-mini-nombre">{t.clase.nombre}</span>
                          <span className="cch-mini-porque">
                            {t.clase.porque_falla
                              ? T.porqueFallas(t.clase.porque_falla,
                                               t.clase.veces_fallada)
                              : T.entrenaEsto(t.clase.entrena)}
                          </span>
                        </span>
                      </button>

                      {/* El coach elige, pero no obliga. */}
                      {t.clase.alternativas?.length > 0 && (
                        <div className="cch-mini-otras">
                          {t.clase.alternativas
                            .filter(a => existeJuego(a.juego))
                            .map(a => (
                              <button
                                key={a.juego}
                                className="cch-mini-otra"
                                style={{ "--juego-color": a.color }}
                                onClick={() => { fxTap(); setMinijuego(a); }}
                                title={T.entrenaEsto(a.entrena)}
                              >
                                <span className="cch-mini-otra-punto" aria-hidden="true" />
                                {a.icono} {a.nombre}
                              </button>
                            ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* La clase que le pone, con su enlace. Un solo clic y entra
                      a entrenar con Spacy: `/lesson/:id` abre directamente en
                      modo IA. */}
                  {/* El juego del glosario con SUS palabras. Se abre encima,
                      sin sacarle de la conversacion: cuando termine, el coach
                      sigue donde estaba. */}
                  {/* La rutina recien montada. No repite la lista —esa esta
                      en la hoja— sino que dice que existe y la abre. */}
                  {t.clase?.tipo === "rutina" && (
                    <button
                      className="cch-clase cch-clase-rutina"
                      onClick={abreRutina}
                    >
                      <span className="cch-clase-eti">{T.rutinaLista}</span>
                      <span className="cch-clase-tit">
                        {T.rutinaTitulo} · {T.rutinaMin(t.clase.minutos)}
                      </span>
                      <span className="cch-clase-cta">{T.rutinaVerla}</span>
                    </button>
                  )}

                  {t.clase?.tipo === "nexus" && t.clase.palabras?.length > 0 && (
                    <button
                      className="cch-clase cch-clase-nexus"
                      onClick={() => setJuego(t.clase.palabras)}
                    >
                      <span className="cch-clase-eti">
                        {t.clase.suyas > 0
                          ? T.palabrasAtragantan(t.clase.suyas)
                          : T.vocabularioDeTuNivel}
                      </span>
                      <span className="cch-clase-tit">Fluency Nexus</span>
                      <span className="cch-clase-cta">{T.machacarVocabulario}</span>
                    </button>
                  )}

                  {/* La sala del gimnasio a la que le manda. Mismo boton que
                      la clase y dentro del hilo: en el coach nada sale de la
                      conversacion.

                      Va ANTES del generico de abajo, y el generico la excluye:
                      `t.clase?.url` pinta cualquier accion que traiga url, y
                      una sala habria salido con la etiqueta "tu entrenamiento
                      de hoy" y el titulo vacio, porque una sala trae `nombre`
                      y no `titulo`. */}
                  {t.clase?.tipo === "sala" && t.clase.url && (
                    <button
                      className="cch-clase cch-clase-sala"
                      onClick={() => navigate(t.clase.url)}
                    >
                      <span className="cch-clase-eti">{T.salaEtiqueta}</span>
                      <span className="cch-clase-tit">
                        {t.clase.icono ? `${t.clase.icono} ` : ""}{t.clase.nombre}
                      </span>
                      <span className="cch-clase-cta">{T.salaEntrar}</span>
                    </button>
                  )}

                  {t.clase?.url && t.clase.tipo !== "sala" && (
                    <button
                      className="cch-clase"
                      onClick={() => navigate(t.clase.url)}
                    >
                      <span className="cch-clase-eti">{T.entrenamientoDeHoy}</span>
                      <span className="cch-clase-tit">
                        {t.clase.titulo}
                        {t.clase.nivel ? ` · ${t.clase.nivel}` : ""}
                      </span>
                      <span className="cch-clase-cta">{T.entrarAEntrenar}</span>
                    </button>
                  )}
                </div>
              ))}
              {/* Los tres puntos y la tarjeta de lo que esta consultando. Los
                  mensajes salen de `coachTextos.js`, asi que cambian de idioma
                  con la pantalla: un alumno que entrena en ingles no espera
                  leyendo espanol. */}
              {pensando && (
                <SpacyThinkingBubble
                  isActive
                  mensajes={T.pensando}
                  avatarIcon={<span aria-hidden="true">🤖</span>}
                />
              )}
              {/* ── EL RECORRIDO DE BIENVENIDA ──────────────────────────
                  Va DENTRO de la conversación, como la tarjeta de cierre de
                  una lección: un cartel encima taparía el chat y habría que
                  quitarlo para seguir; aquí se desplaza con los mensajes y se
                  puede ignorar sin cerrar nada.

                  El Coach no HACE el recorrido —sólo dice cuándo empieza—.
                  Toda su lógica vive en `components/recorrido/`, aparte, para
                  no añadir estado a una pantalla que ya lleva 34. */}
              {recorrido && (
                <RecorridoNSL
                  modo={recorrido}
                  onCerrar={() => setRecorrido(null)}
                  onHabla={setHablando}
                />
              )}

              <div ref={finalRef} />
            </section>
            </ScanBorderWrapper>

            {/* Lo que puede pedirle, con el diseno de Spacy en lecciones. Va
                SIEMPRE visible y no solo al principio: es el menu del
                gimnasio, no un cartel de bienvenida. */}
            <CoachAcciones
              acciones={acciones}
              textos={T}
              senalDePlegado={plegarBotonera}
              disabled={pensando || seQuedoSinTokens}
              onAccion={a => {
                // `local` = se resuelve en esta pantalla y NO se manda al
                // coach. Sin esta linea, "Como se entrena aqui" gastaria una
                // llamada a la IA para contar algo que siempre dice lo mismo.
                if (a.local && a.id === "recorrido") {
                  setRecorrido("menu");
                  apartaLaBotonera();
                }
                // La rutina que ya existe se abre; no se vuelve a pedir.
                else if (a.id === "rutina" && rutina?.pasos?.length) abreRutina();
                // Y "Entrenar" con rutina abierta entra directo a lo siguiente.
                else if (a.atajo) abreEstacion(a.atajo);
                else manda(a.mensaje);
              }}
            />

            {/* Lo que va oyendo, mientras lo oye. Sin esto el alumno habla a
                ciegas y no sabe si el microfono le esta cogiendo.

                Va en su propia fila y no flotando sobre la conversacion: ahi
                tapaba el ultimo mensaje del coach, que es justo lo que el
                alumno esta contestando. */}
            {oido.escuchando && (
              <div className="cch-oyendo">
                <span className="cch-oyendo-punto" />
                {oido.parcial || T.teEscucho}
              </div>
            )}

            {/* ── Lo que escribe el alumno ─────────────────────────────────
                Es la MISMA caja que la de Spacy en lecciones, hasta el ultimo
                pixel: bandeja levantada por fuera, hueco hundido por dentro,
                los tres botones redondos DENTRO del hueco y el contador
                debajo, alineado a la derecha.

                Se replica en `cch-` en vez de importar `SpeakingExercise.css`
                porque ese fichero son 1.900 lineas de pantalla entera —avatar,
                pantalla completa, barra de pasos— y traerlo aqui es repetir el
                choque de `pp-` que dejo la pagina de precios torcida.
                ─────────────────────────────────────────────────────────── */}
            {/* ── SIN TOKENS ────────────────────────────────────────────
                Va justo encima de la caja de escribir, que es donde el alumno
                va a ir cuando quiera hablar. Y NO tapa la pantalla: puede
                seguir abriendo su rutina, sus juegos y su historial. Lo unico
                que se apaga es hablar, que es lo unico que cuesta dinero.
                Bloquear todo por no poder pagar UNA cosa castiga de mas. */}
            {seQuedoSinTokens && (
              <section className="cch-sintokens" role="status">
                <span className="cch-sintokens-icono" aria-hidden="true">⚡</span>
                <span className="cch-sintokens-texto">
                  <b>{T.sinTokensTitulo}</b>
                  <span>{T.sinTokensCuerpo}</span>
                </span>
                <button className="cch-sintokens-boton"
                        onClick={() => navigate("/plans?tab=tokens")}>
                  {T.sinTokensBoton}
                </button>
              </section>
            )}

            <section className="cch-entrada">
              <div className="cch-caja">
                <textarea
                  ref={textoRef}
                  className="cch-texto"
                  value={texto}
                  /* El tope va en el setter y no solo en maxLength: maxLength
                     no actúa cuando el texto entra por programa —dictado, voz—
                     y se quita desde las herramientas del navegador en dos
                     clics. Aquí pasan todos los caminos. */
                  onChange={ev => {
                    setTexto(ev.target.value.slice(0, MAX_MENSAJE));
                    creceSola(ev.target);
                  }}
                  onKeyDown={ev => {
                    if (ev.key === "Enter" && !ev.shiftKey) {
                      ev.preventDefault();
                      manda(texto);
                    }
                  }}
                  maxLength={MAX_MENSAJE}
                  placeholder={seQuedoSinTokens ? T.sinTokensInput
                    : oido.escuchando ? T.teEscucho : T.escribe}
                  disabled={pensando || seQuedoSinTokens}
                />

                <div className="cch-iconos">
                  {oido.soportado && (
                    <button
                      className={`cch-icono ${oido.escuchando ? "cch-oyendo-btn" : ""}`}
                      onClick={() => (oido.escuchando ? oido.parar() : oido.escuchar())}
                      disabled={pensando || seQuedoSinTokens}
                      title={oido.escuchando ? T.parar : T.hablar}
                      aria-label={oido.escuchando ? T.pararDeHablar : T.hablarAlCoach}
                    >
                      {oido.escuchando ? <Square size={18} /> : <Mic size={18} />}
                      {oido.escuchando && <span className="cch-punto-rec" />}
                    </button>
                  )}

                  {/* Solo cuando hay algo que borrar. Un aspa permanente sobre
                      una caja vacia es un boton que nunca hace nada. */}
                  {texto && (
                    <button
                      className="cch-icono cch-limpiar"
                      onClick={() => {
                        setTexto("");
                        if (textoRef.current) textoRef.current.style.height = "40px";
                      }}
                      aria-label={T.limpiarTexto}
                    >
                      ×
                    </button>
                  )}

                  <button
                    className="cch-icono cch-enviar"
                    onClick={() => manda(texto)}
                    disabled={pensando || seQuedoSinTokens
                              || !texto.trim() || oido.escuchando}
                    aria-label={T.enviarMensaje}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>

              {/* El contador va DEBAJO y no dentro: dentro se metia bajo el
                  microfono y el enviar, justo donde ya hay tres controles.
                  Y se ve SIEMPRE —gris mientras sobra sitio, ámbar al 80%,
                  rojo al llegar—: un límite que no se puede ver antes de
                  cruzarlo no está puesto para quien escribe. */}
              <div
                className={
                  "cch-contador" +
                  (texto.length >= MAX_MENSAJE
                    ? " cch-contador-tope"
                    : avisaDelTope(texto.length, MAX_MENSAJE)
                      ? " cch-contador-cerca"
                      : "")
                }
              >
                {texto.length}/{MAX_MENSAJE}
              </div>
            </section>

            {avisoOido && <div className="cch-aviso">{avisoOido}</div>}
          </>
        )}

        <button className="cch-volver" onClick={() => navigate("/entrenar")}>
          {T.volver}
        </button>
        </div>
      </main>

      {confeti && <Confeti onFin={() => setConfeti(false)} />}

      {juego && (
        <MiniGameModal words={juego} onClose={() => setJuego(null)} />
      )}

      {rutinaAbierta && (
        <CoachRutina
          rutina={rutina}
          historial={historial}
          textos={T}
          onAbrir={abreEstacion}
          onPedir={pideAlCoach}
          onDescartar={descartaRutina}
          onCerrar={() => { fxVolver(); setRutinaAbierta(false); }}
        />
      )}

      {minijuego && (
        <CoachJuego
          juego={minijuego.juego}
          nombre={minijuego.nombre}
          icono={minijuego.icono}
          color={minijuego.color}
          onCerrar={() => { fxVolver(); setMinijuego(null); }}
        />
      )}
    </div>
  );
}
